import { createSlice } from '@reduxjs/toolkit';

const storedCart = localStorage.getItem('nm_cart');
let parsedCart = storedCart ? JSON.parse(storedCart) : { items: [] };

// Normalize items on rehydration:
// 1. Set is_box = true for any item that has a selectionsKey (authoritative box marker)
// 2. Drop items that have is_box:true but no selectionsKey — they were stored broken
//    and can't be recovered (no selections data, no correct price)
parsedCart.items = (parsedCart.items || [])
  .map(item => ({ ...item, is_box: item.is_box === true || !!item.selectionsKey }))
  .filter(item => !item.is_box || !!item.selectionsKey);

const initialCart = parsedCart;

function persist(state) {
  localStorage.setItem('nm_cart', JSON.stringify({ items: state.items }));
}

const cartSlice = createSlice({
  name: 'cart',
  initialState: initialCart,
  reducers: {
    addToCart(state, action) {
      const { product, quantity = 1, boxSelections = null } = action.payload;

      // A box item is identified by boxSelections being provided — never rely solely on product.is_box
      // which can be lost/falsy due to API response shape or spread issues
      if (boxSelections && boxSelections.length > 0) {
        // Box products: each configuration is unique — always add as a new entry
        // identified by product._id + a snapshot of the selections
        const selectionsKey = JSON.stringify(
          boxSelections
            .filter(s => s.quantity > 0)
            .map(s => ({ id: s.product._id, qty: s.quantity }))
            .sort((a, b) => a.id.localeCompare(b.id))
        );

        const existing = state.items.find(
          i => i._id === product._id && !!i.selectionsKey && i.selectionsKey === selectionsKey
        );

        if (existing) {
          // Box products: no stock cap on the box itself — sub-item stock validated at checkout
          existing.quantity = existing.quantity + quantity;
        } else {
          // For box products the price is pre-computed from item selections (never 0)
          const boxPrice = product.price;  // already set to computedPrice by handleBoxAddToCart
          const activeSelections = boxSelections.filter(s => s.quantity > 0);
          const boxSize = activeSelections.reduce((s, sel) => s + sel.quantity, 0);
          state.items.push({
            _id: product._id,
            names: product.names,
            price: boxPrice,
            original_price: boxPrice,
            banner: product.banner,
            stock: product.stock,
            quantity,
            is_box: true,
            box_size: boxSize,
            // Store selection details for display in Cart
            boxSelections: activeSelections.map(s => ({
              _id: s.product._id,
              names: s.product.names,
              banner: s.product.banner,
              price: s.product.price,
              quantity: s.quantity,
            })),
            selectionsKey,
          });
        }
      } else {
        // Regular product (or variant of a product)
        // Different variant selections are separate cart entries
        const existing = state.items.find(
          i => i._id === product._id && !i.is_box &&
               (i._variantIdx ?? null) === (product._variantIdx ?? null)
        );
        if (existing) {
          existing.quantity = Math.min(existing.quantity + quantity, product.stock);
        } else {
          state.items.push({
            _id: product._id,
            names: product.names,
            price: product.sale_price || product.price,
            original_price: product.price,
            banner: product.banner,
            stock: product.stock,
            quantity,
            is_box: false,
            _variantLabel: product._variantLabel || null,
            _variantIdx:   product._variantIdx   ?? null,
          });
        }
      }

      persist(state);
    },

    removeFromCart(state, action) {
      // payload: _id string for regular items, { _id, selectionsKey } for box items,
      // or { _id, _variantIdx } for variant items
      const payload = action.payload;
      if (typeof payload === 'object' && payload.selectionsKey) {
        state.items = state.items.filter(
          i => !(i._id === payload._id && i.selectionsKey === payload.selectionsKey)
        );
      } else if (typeof payload === 'object' && payload._variantIdx != null) {
        state.items = state.items.filter(
          i => !(i._id === payload._id && i._variantIdx === payload._variantIdx)
        );
      } else {
        state.items = state.items.filter(i => i._id !== payload);
      }
      persist(state);
    },

    updateQuantity(state, action) {
      const { id, quantity, selectionsKey, _variantIdx } = action.payload;
      const item = selectionsKey
        ? state.items.find(i => i._id === id && i.selectionsKey === selectionsKey)
        : _variantIdx != null
          ? state.items.find(i => i._id === id && i._variantIdx === _variantIdx)
          : state.items.find(i => i._id === id && !i.is_box && i._variantIdx == null);

      if (item) {
        if (quantity <= 0) {
          state.items = selectionsKey
            ? state.items.filter(i => !(i._id === id && i.selectionsKey === selectionsKey))
            : _variantIdx != null
              ? state.items.filter(i => !(i._id === id && i._variantIdx === _variantIdx))
              : state.items.filter(i => !(i._id === id && !i.is_box && i._variantIdx == null));
        } else {
          // Box products don't have meaningful stock — their limit is in sub-items.
          // Only clamp to stock for regular products that have stock > 0.
          const maxQty = (!item.is_box && item.stock > 0) ? item.stock : Infinity;
          item.quantity = Math.min(quantity, maxQty);
        }
      }
      persist(state);
    },

    clearCart(state) {
      state.items = [];
      persist(state);
    },
  },
});

export const { addToCart, removeFromCart, updateQuantity, clearCart } = cartSlice.actions;

// Selectors
export const selectCartItems    = state => state.cart.items;
export const selectCartCount    = state => state.cart.items.reduce((s, i) => s + i.quantity, 0);
export const selectCartSubtotal = state =>
  state.cart.items.reduce((s, i) => s + i.price * i.quantity, 0);

export default cartSlice.reducer;
